/* global google, InfoBox, AppSettings */

/**
 * This is initalization for Google Map
 *
 * @author Tamara Bazko
 */
import $ from 'jquery';
import createLogger from 'components/logger/Logger';
import EventTypes from 'components/EventTypes';
import matchBreakpoint from 'components/utils/matchBreakpoint';
import iconSpritesheet from 'components/spritesheets/markers.json';
import iconRetinaSpritesheet from 'components/spritesheets/markers@2x.json';
import GPSUtils from 'components/utils/GPS';
import MarkerClusterer from 'markerclusterer';
import 'jquery.stickyTrigger';
import 'jquery.extendPrototype';
import 'components/domutils/Element.jQuery';

const Logger = createLogger('googleMap');

const $document = document.jq;
const labels = window.labels.storeFinder;
const DISTANCE_UNIT = labels && labels.distanceUnit && labels.distanceUnit.toLowerCase();
const maxWidthInfoWindow = 320;

const GoogleMap = {
  $map: undefined,
  mapCanvas: undefined,
  mapCanvasBounds: undefined,

  _$storeFinderBar: undefined, // SF abbreviation for StoreFinder
  _storeFinderBarMargin: undefined,
  _storeFinderBarWidth: undefined,

  _bounds: undefined,
  _defaultZoomLevel: 8,
  _geocoder: undefined,
  _infoWindow: undefined,
  _markerClusterer: undefined,
  _offsetLeft: -196,
  _rightPixelPoint: undefined,
  _draggable: true,
  markerIcon: undefined,
  brandIcon: undefined,
  outletIcon: undefined,
  offsetMapListener: undefined,

  /**
   * Bind marker events
   * @param   {Object}  Marker    Google object
   */
  bindMarkerEvents(marker) {
    google.maps.event.addListener(marker, 'click', this._triggerEvent.bind(this, marker, 'click'));
    google.maps.event.addListener(
      marker,
      'mouseover',
      this._triggerEvent.bind(this, marker, 'mouseover')
    );
    google.maps.event.addListener(
      marker,
      'mouseout',
      this._triggerEvent.bind(this, marker, 'mouseout')
    );
  },

  /**
   * Create marker icon
   * @param  {string} name   Name of the icon
   *                         Image should exist in _static/img/sprites/markers/`name`.png
   * @param {boolean}
   * @return {object}        Object containing icon properties.
   */
  createMarkerIcon(name, bottomAnchor) {
    if (iconSpritesheet && iconSpritesheet.items && iconSpritesheet.items[name]) {
      const hasRetinaScreen = window.devicePixelRatio && window.devicePixelRatio > 1.5;
      const spritesheet = hasRetinaScreen ? iconRetinaSpritesheet : iconSpritesheet;

      const item = spritesheet.items[name];
      const markerPath = AppSettings.assetPath;
      const multiplier = hasRetinaScreen ? 2 : 1;
      const size = new google.maps.Size(item.width / multiplier, item.height / multiplier);
      const scaledSize = new google.maps.Size(
        spritesheet.width / multiplier,
        spritesheet.height / multiplier
      );
      const aspectRatio = item.width / item.height;
      let anchor;

      if (aspectRatio < 1 || bottomAnchor) {
        anchor = new google.maps.Point(size.width / 2, size.height - multiplier * 2);
      } else {
        anchor = new google.maps.Point(size.width / 2, size.height / 2);
      }

      const origin = new google.maps.Point(item.x / multiplier, item.y / multiplier);
      const result = {
        anchor,
        origin,
        size,
        scaledSize,
        url: (markerPath + spritesheet.image).replace(/\/+/g, '/'),
      };

      Logger.debug('createMarkerIcon', name, result);
      return result;
    }
    Logger.warn('createMarkerIcon: cannot create icon for:', name);
    return {};
  },

  /**
   * Close info window
   */
  closeInfoWindow() {
    this._infoWindow.close();
  },

  /**
   * Extend map bounds
   * @param  {Object}  Marker   google.maps.Marker
   */
  extendBounds(latLng) {
    if (!this.mapCanvasBounds) {
      this.mapCanvasBounds = this.getLatLngBounds();
    }

    this.mapCanvasBounds.extend(latLng);
    return this.mapCanvasBounds;
  },

  /**
   * Fit bounds
   * @param  {Object}  bounds   google.maps.LatLngBounds
   */
  fitBounds(bounds) {
    this.mapCanvas.fitBounds(bounds);
    if (this.mapCanvas.getZoom() > 14) {
      this.setMapZoom(14);
    }

    if (matchBreakpoint('medium')) {
      google.maps.event.removeListener(this.offsetMapListener);
      this.offsetMapListener = google.maps.event.addListenerOnce(
        this.mapCanvas,
        'idle',
        this.offsetMap.bind(this, bounds)
      );
    } else {
      this.mapCanvas.fitBounds(bounds);
      this._clusterDisappearanceFix();
    }
  },

  /**
   * Focusing on provided country
   * @param  {String}  country   Country name
   */
  focusOnCountry(country) {
    this._geocoder.geocode(
      {
        address: country,
      },
      function (results, status) {
        if (status === google.maps.GeocoderStatus.OK) {
          this.fitBounds(results[0].geometry.bounds);
        }
      }.bind(this)
    );
  },

  /**
   * Translating points to pixels
   * @param  {Object}  latLng   google.maps.LatLng
   * @return {Point}   Point    object with pixel points on x and y axis
   */
  fromLatLngToPixelPoint(latLng) {
    const mapBounds = this.mapCanvas.getBounds();
    const mapProjection = this.mapCanvas.getProjection();
    const scale = Math.pow(2, this.mapCanvas.getZoom());
    const nw = this.getLatLng(mapBounds.getNorthEast().lat(), mapBounds.getSouthWest().lng());
    const worldCoordinateNW = mapProjection.fromLatLngToPoint(nw);
    const worldCoordinate = mapProjection.fromLatLngToPoint(latLng);
    return new google.maps.Point(
      Math.floor((worldCoordinate.x - worldCoordinateNW.x) * scale),
      Math.floor((worldCoordinate.y - worldCoordinateNW.y) * scale)
    );
  },

  /**
   * Offset map, so that bounds of searched place or markers group doesn't appear underneath
   * storefinder overlay
   * @param  {Object}  bounds   google.maps.LatLngBounds
   */
  offsetMap(bounds) {
    if (bounds && matchBreakpoint('medium')) {
      if (!this._$storeFinderBar) {
        this._$storeFinderBar = document.getElementsByClassName('js-storeFinder-bar').jq;
        this._storeFinderBarMargin = parseFloat(this._$storeFinderBar.css('marginLeft')) || 0;
      }

      // Width of bar can be changed on resize
      this._storeFinderBarWidth =
        this._$storeFinderBar.width() + this._storeFinderBarMargin * 2 || 0;

      if (this._bounds !== bounds) {
        this._bounds = bounds;
        this._leftCoords = this._bounds.getSouthWest();
        this._rightCoords = this._bounds.getNorthEast();
      }

      // Map width can change on resize
      this._rightPixelPoint = this.$map.outerWidth();

      // Get pixel position of leftmost and rightmost points of the passed bounds shape
      const leftMost = this.fromLatLngToPixelPoint(this._leftCoords).x;
      const rightMost = this.fromLatLngToPixelPoint(this._rightCoords).x;

      // Offset from left, same as the leftmost bounds point
      const leftOffset = leftMost;
      const rightOffset = this._rightPixelPoint - rightMost - this._storeFinderBarMargin;
      const offsetFromBar = leftOffset - this._storeFinderBarWidth;
      const offsetsTotal = leftOffset + rightOffset;

      // If offsets total is less than SF bar width, bounds can't fit
      if (offsetsTotal < this._storeFinderBarWidth && offsetsTotal >= 0) {
        this.mapCanvas.setZoom(this.mapCanvas.getZoom() - 1);

        this.offsetMap(bounds);

        // In other cases, bounds can fit and be center aligned
      } else if (offsetFromBar !== rightOffset) {
        const mapOffset = Math.round((offsetFromBar + rightOffset) / 2 - offsetFromBar);

        this.mapCanvas.panBy(-mapOffset, 0);

        const centerMarkerX = (leftMost + rightMost) / 2 + mapOffset;

        // when there's not enough space for the info window to show
        // next to the center marker, zoom out by 1
        if (centerMarkerX - this._storeFinderBarWidth < maxWidthInfoWindow) {
          this.mapCanvas.setZoom(this.mapCanvas.getZoom() - 1);
        }
      }
    }
  },

  /**
   * Check if element fits in visible bounds (screen view)
   * @param  {Object}  element   Map element
   */
  fitVisibleBounds(marker) {
    const bounds = this.mapCanvas.getBounds();
    return bounds.contains(marker.getPosition());
  },

  /**
   * Update distance from user location to stores OR from searched place to stores
   * and trigger event
   *
   * @param  {Object}   coordinates     Latitude and longitude
   * @param  {Array}    stores          Array of store objects
   */
  distanceUpdate(stores, coordinates) {
    GPSUtils.setDistances(coordinates, stores, DISTANCE_UNIT);
    GPSUtils.sortOnDistance(stores);
    $document.trigger(EventTypes.STOREFINDER_DISTANCE_UPDATE, {
      stores,
      origin: coordinates,
    });
  },

  /**
   * Create and get InfoBox
   * @param  {Object}  marker       Google object, marker for which InfoBox is created
   * @param  {String}  content      Content for InfoBox
   * @param  {String}  breakpoint
   * @return {Object}  InfoBox      Google object
   */
  getInfoWindow(marker, content, breakpoint) {
    const pxPoint = this.fromLatLngToPixelPoint(marker.getPosition()).x;
    const mapWidth = this.$map.width();
    const distanceToEdge = mapWidth - pxPoint;
    let pixelOffset;
    let boxClass;
    const brandOffset = 40;
    const regularOffset = 30;

    if (distanceToEdge < 350) {
      pixelOffset = this.getSize(
        marker.store.isBrandStore
          ? -maxWidthInfoWindow - brandOffset
          : -maxWidthInfoWindow - regularOffset,
        -44
      );
      boxClass = 'storeFinder-infoWindow storeFinder-infoWindow--flipped';
    } else {
      pixelOffset = this.getSize(marker.store.isBrandStore ? brandOffset : regularOffset, -44);
      boxClass = 'storeFinder-infoWindow';
    }

    pixelOffset = matchBreakpoint('medium') ? pixelOffset : '0, 0';

    if (this._infoWindow) {
      this._infoWindow.pixelOffset_ = pixelOffset;
      this._infoWindow.boxClass_ = boxClass;
    } else {
      this._infoWindow = new InfoBox({
        boxClass,
        maxWidth: matchBreakpoint(breakpoint) ? `${maxWidthInfoWindow}px` : 0,
        disableAutoPan: true,
        closeBoxURL: '',
        content,
        pixelOffset,
      });
    }

    return this._infoWindow;
  },

  /**
   * Get latitude and longitude Google object
   * @param  {Number}  lat      latitude
   * @param  {Number}  lon      longitude
   * @return {Object}  LatLng   Google object
   */
  getLatLng(lat, lon) {
    return new google.maps.LatLng(lat, lon);
  },

  /**
   * Get latitude and longitude Google object
   * @param  {Number}  lat      latitude
   * @param  {Number}  lon      longitude
   * @return {Object}  LatLng   Google object
   */
  getUserLatLng(lat, lon) {
    return this.getLatLng(lat, lon);
  },

  /**
   * Get latitude and longitude bounds of map object
   * @return  {Object}  LatLngBounds   Google object
   */
  getLatLngBounds() {
    this.mapCanvasBounds = new google.maps.LatLngBounds();
    return this.mapCanvasBounds;
  },

  /**
   * Get marker clusterer
   * @return  {Object}  _markerClusterer    Marker clusterer object
   */
  getMarkerClusterer() {
    if (this._markerClusterer) {
      return this._markerClusterer;
    }
    return false;
  },

  /**
   * Get level of current map zoom
   * @return  {Number}  zoom level
   */
  getMapZoom() {
    return this.mapCanvas.getZoom();
  },

  /**
   * Get marker position in coordinates
   * @param   {Object}  Marker    Google object
   * @return  {Object}  LatLng    Google object
   */
  getMarkerPosition(marker) {
    return marker.position;
  },

  /**
   * Get max possible marker z-index, set in Google
   * @return  {Number}  z-index
   */
  getMarkerMaxZIndex() {
    return google.maps.Marker.MAX_ZINDEX;
  },

  /**
   * Get offset left (width of storefinder list overlay)
   * @return  {Number}  offsetLeft
   */
  getOffsetLeft() {
    return this._offsetLeft;
  },

  /**
   * Get map size
   * @param  {Number}  width
   * @param  {Number}  height
   * @return {Object}  Size    Google object
   */
  getSize(width, height) {
    return new google.maps.Size(width, height);
  },

  /**
   * Create and get store marker
   * @param  {Object}  store    store object with properties
   * @return {Object}  Marker   marker for store
   */
  getStoreMarker(store) {
    const coordinates = this.getLatLng(store.latitude, store.longitude);
    const markerIcon = this.getStoreIcon(store);

    const marker = new google.maps.Marker({
      map: this.mapCanvas,
      clickable: true,
      title: store.name,
      position: coordinates,
      store_id: store.store_id,
      distance: store.meters || store.distance,
      icon: markerIcon,
    });

    this.bindMarkerEvents(marker);
    return marker;
  },

  getStoreIcon(store) {
    const customIcons = AppSettings.storeFinderIcons ? AppSettings.storeFinderIcons : false;
    if (!this.markerIcon) {
      this.markerIcon = customIcons
        ? this.createMarkerIcon(customIcons.regularMarkerName)
        : this.createMarkerIcon('store');
    }
    if (!this.brandIcon) {
      this.brandIcon = customIcons
        ? this.createMarkerIcon(customIcons.brandMarkerName)
        : this.createMarkerIcon('g-star');
    }

    if (!this.outletIcon) {
      this.outletIcon = customIcons
        ? this.createMarkerIcon(customIcons.brandMarkerName)
        : this.createMarkerIcon('g-star-outlet');
    }

    if (store.isBrandStore) {
      return this.brandIcon;
    } else if (store.isOutletStore) {
      return this.outletIcon;
    } else {
      return this.markerIcon;
    }
    return store.isBrandStore ? this.brandIcon : this.markerIcon;
  },

  setStores(eventData) {
    $document.trigger(EventTypes.STOREFINDER_GET_STORES, eventData);
  },

  /**
   * Create and return marker
   * @param  {Object}  LatLng   google.maps.LatLng
   * @param  {Boolean} Visibility   indicates marker visibility (e.g. user location marker is invisible)
   * @param  {Object=}  Icon        (optional) icon for marker
   * @return {Object}  Marker       marker points on passed location
   */
  getMarker(latLng, visibility, icon) {
    const marker = new google.maps.Marker({
      map: this.mapCanvas,
      position: latLng,
      visible: visibility,
      icon: icon ? icon : '',
    });

    return marker;
  },

  /**
   * Load map and wait for initializing it
   * @param  {Object}  $map    jQuery object, container in which map exists
   * @return {jQuery.Deferred} jQuery deferred object
   */
  loadMap(settings) {
    const deferred = $.Deferred();
    this.$map = settings.$map;
    this._draggable = settings.draggable;

    window.GSRD.loadAsset(
      `//maps.google.com/maps/api/js?key=${AppSettings.googleMapsKey}&language=${AppSettings.language}&libraries=places`
    ).then(() => {
      import(/* webpackChunkName: "import--vendor-infobox" */ 'vendor/infobox')
        .then(() => {
          this._initializeMap().done(deferred.resolve.bind(deferred));
        })
        .catch((reason) => Logger.warn(`Unable to load Google Maps. Reason: ${reason}`));
    });

    return deferred;
  },

  loadStores(data) {
    this.$map.trigger(EventTypes.STOREFINDER_LOAD_STORES, data);
  },

  loadStoresByCoordinates(data) {
    this.$map.trigger(EventTypes.STOREFINDER_LOAD_STORES_BY_COORDINATES, data);
  },

  /**
   * Changes the center of the map by the given distance in pixels
   * @param  {Number}  xNumber    horizontal value
   * @param  {Number}  yNumber    vertical value
   */
  mapPanBy(xNumber, yNumber) {
    this.mapCanvas.panBy(xNumber, yNumber);
  },

  /**
   * Changes the center of the map to the given LatLng
   * @param  {Object}  latLng   google.maps.LatLng
   */
  mapPanTo(latLng) {
    this.mapCanvas.panTo(latLng);
  },

  /**
   * Open info window (InfoBox)
   * @param   {Object}  Marker    Google object
   */
  openInfoWindow(marker) {
    this._infoWindow.open(this.mapCanvas, marker);
  },

  /**
   * Empty object with markers (which are stored by store_id),
   * prevents memory leak
   * @param   {Object}  markers    Markers object, stored by store_id
   */
  removeStoreMarkers(markers) {
    return {};
  },

  /**
   * If change of content in info window is needed
   * @param  {Object}  infoWindow
   * @param  {String}  content
   */
  setInfoWindowContent(infoWindow, content) {
    infoWindow.setContent(content);
  },

  /**
   * Set map
   * @param   {Object}  Marker    Google object
   * @param   {Object}  Map       Google object
   */
  setMap(marker, map) {
    marker.setMap(map);
    if (map === null) {
      marker.setVisible(false);
      google.maps.event.clearInstanceListeners(marker);
    }
  },

  /**
   * Set map center
   * @param   {Object}  latLng    Google object
   */
  setMapCenter(latLng) {
    this.mapCanvas.setCenter(latLng);
  },

  /**
   * Set map zoom level
   * @param  {Number}  zoomLevel
   */
  setMapZoom(zoomLevel) {
    this.mapCanvas.setZoom(zoomLevel);
  },

  /**
   * Set marker icon position
   * @param   {Object}  Marker    Google object
   * @param   {Object}  latLng    Google object
   */
  setMarkerPosition(marker, latLng) {
    marker.setPosition(latLng);
  },

  /**
   * Set marker's visibility
   * @param  {Array}   markerCollection     collection of markers
   * @param  {Boolean}  visible
   */
  setMarkersVisibility(markerCollection, visible) {
    for (const marker in markerCollection) {
      markerCollection[marker].setVisible(visible);
    }

    if (this._markerClusterer) {
      this._setClusterVisibility(markerCollection, visible);
    }
  },

  /**
   * Set clusters's visibility
   * @param  {Array}   markerCollection     collection of markers to add or remove from cluster
   * @param  {Boolean}  visible
   */
  _setClusterVisibility(markerCollection, visible) {
    if (visible) {
      this._markerClusterer.addMarkers(markerCollection);
    } else {
      this._markerClusterer.clearMarkers();
    }
  },

  /**
   * Set z-index for marker
   * @param  {Object}  latLng   google.maps.LatLng
   * @param  {Number}  zIndex
   */
  setMarkerZIndex(marker, zIndex) {
    marker.setZIndex(zIndex);
  },

  /**
   * Set marker clusters
   * @param   {Object}  markerCollection    collection of markers
   */
  setClustering(markerCollection) {
    if (this._markerClusterer) {
      this._markerClusterer.clearMarkers();
    }

    const textSize = 14;
    const textColor = '#fff';
    const transparentPic =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    const defaultSize = 30;

    const styles = [
      {
        height: defaultSize,
        textColor,
        textSize,
        width: defaultSize,
        url: transparentPic,
      },
      {
        height: defaultSize + 5,
        textColor,
        textSize,
        width: defaultSize + 5,
        url: transparentPic,
      },
      {
        height: defaultSize + 10,
        textColor,
        textSize,
        width: defaultSize + 10,
        url: transparentPic,
      },
    ];

    this._markerClusterer = new MarkerClusterer(this.mapCanvas, markerCollection, {
      minimumClusterSize: 3,
      styles,
      zoomOnClick: true,
    });

    this._markerClusterer.setClusterClass('storeFinder-map-cluster');
    this._markerClusterer.setMaxZoom(11);
  },

  /**
   * Trigger API events
   * @param   {Object}  mapObject   Google maps object
   * @param   {String}  eventType   name of event
   */
  triggerAPIevent(mapObject, eventType) {
    google.maps.event.trigger(mapObject, eventType);
  },

  /**
   ******************* PRIVATE METHODS *******************
   *
   * Bind events
   */
  _bindEvents() {
    google.maps.event.addListener(
      this.mapCanvas,
      'bounds_changed',
      this._triggerEvent.bind(this, this.mapCanvas, 'bounds_changed')
    );
    google.maps.event.addListener(this.mapCanvas, 'zoom_changed', this._onZoomChanged.bind(this));
  },

  /**
   * Fixing disappearance of clusters
   * Issue described here: https://code.google.com/p/google-maps-utility-library-v3/issues/detail?id=252
   */
  _clusterDisappearanceFix() {
    const mapcenter = this.mapCanvas.getCenter();
    if (!mapcenter) {
      return;
    }
    const latLng = this.getLatLng(mapcenter.lat() + 0.0000001, mapcenter.lng());
    this.setMapCenter(latLng);
  },

  /**
   * Create google maps instance with configured options
   */
  _initializeMap() {
    const deferred = $.Deferred();

    if (this.mapCanvas) {
      Logger.warn('initializeMap: Map already initialized');
      deferred.resolve();
    } else {
      Logger.debug('Initialize map');

      // Google Maps configuration
      // See: https://developers.google.com/maps/documentation/javascript/reference#MapOptions
      const mapOptions = {
        draggable: this._draggable,
        fullscreenControl: false,
        mapTypeControl: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        maxZoom: false,
        overviewMapControl: false,
        panControl: false, // deprecated by Google?
        rotateControl: false,
        scaleControl: false,
        streetViewControl: false,
        streetViewControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_RIGHT,
        },
        zoomLevel: this._defaultZoomLevel,
        zoomControl: false,
        zoomControlOptions: {
          position: google.maps.ControlPosition.TOP_RIGHT,
          style: google.maps.ZoomControlStyle.SMALL,
        },
      };
      this.mapCanvas = new google.maps.Map(this.$map.get(0), mapOptions);

      this._geocoder = new google.maps.Geocoder();
      this._bindEvents();
      deferred.resolve();
    }

    return deferred;
  },

  _onZoomChanged() {
    if (!this._markerClusterer) {
      return;
    }

    let gridSize;
    const zoomLevel = this.getMapZoom();

    if (zoomLevel <= 4) {
      gridSize = 40;
    } else if (zoomLevel === 5) {
      gridSize = 50;
    } else if (zoomLevel <= 7) {
      gridSize = 45;
    } else if (zoomLevel === 8) {
      gridSize = 90;
    } else if (zoomLevel === 9) {
      gridSize = 130;
    } else if (zoomLevel <= 11) {
      gridSize = 90;
    } else {
      gridSize = 70;
    }

    this._markerClusterer.setGridSize(gridSize);
  },

  /**
   * Trigger events, used to conceal API event listeners
   * @param   {Object}  Marker      Google object
   * @param   {String}  eventType   name of event
   */
  _triggerEvent(mapObject, eventType) {
    switch (eventType) {
      case 'click':
        this.$map.trigger(EventTypes.STOREFINDER_MARKER_CLICK, {
          marker: mapObject,
        });
        break;
      case 'mouseover':
        this.$map.trigger(EventTypes.STOREFINDER_MARKER_MOUSEOVER, {
          marker: mapObject,
        });
        break;
      case 'mouseout':
        this.$map.trigger(EventTypes.STOREFINDER_MARKER_MOUSEOUT, {
          marker: mapObject,
        });
        break;
      case 'bounds_changed':
        this.$map.trigger(EventTypes.STOREFINDER_MAP_BOUNDS_CHANGED);
        break;
    }
  },
};
export default GoogleMap;
